/**
 * Domain constants
 * Enums, configs et helpers liés au modèle de données.
 */

import { Icons } from "./icons";

// ─── Labels calendrier ──────────────────────────────────────────

export const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
export const MONTH_LABELS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

// ─── User types ─────────────────────────────────────────────────

export const USER_TYPES = {
  MEMBER: "member",
  TEACHER: "teacher",
  BOARD_MEMBER: "board_member",
  HELPER: "helper",
  STUDENT: "student",
};

const USER_TYPE_CONFIG = {
  [USER_TYPES.MEMBER]: { value: USER_TYPES.MEMBER, label: "Membre" },
  [USER_TYPES.TEACHER]: { value: USER_TYPES.TEACHER, label: "Enseignant" },
  [USER_TYPES.BOARD_MEMBER]: {
    value: USER_TYPES.BOARD_MEMBER,
    label: "Membre du bureau",
  },
  [USER_TYPES.HELPER]: {
    value: USER_TYPES.HELPER,
    label: "Assistant non praticant",
  },
  [USER_TYPES.STUDENT]: { value: USER_TYPES.STUDENT, label: "Élève" },
};

export const getUserTypeConfig = (type) => USER_TYPE_CONFIG[type] || null;
export const getUserTypeLabel = (type) =>
  getUserTypeConfig(type)?.label ?? type;

// ─── Activity status (actif / inactif sur un membre) ───────────

export const STATUS_TYPES = { ACTIVE: "active", INACTIVE: "inactive" };

const ACTIVITY_STATUS_CONFIG = {
  [STATUS_TYPES.ACTIVE]: { value: "active", label: "Actif" },
  [STATUS_TYPES.INACTIVE]: { value: "inactive", label: "Inactif" },
};

export const getActivityStatusConfig = (type) =>
  ACTIVITY_STATUS_CONFIG[type] || null;
export const getActivityStatusLabel = (type) =>
  getActivityStatusConfig(type)?.label ?? type;

// ─── Recurrence ─────────────────────────────────────────────────

export const RECURRENCE_FREQUENCIES = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
};

const RECURRENCE_FREQUENCY_CONFIG = {
  [RECURRENCE_FREQUENCIES.DAILY]: { value: "daily", label: "Quotidien" },
  [RECURRENCE_FREQUENCIES.WEEKLY]: { value: "weekly", label: "Hebdomadaire" },
  [RECURRENCE_FREQUENCIES.MONTHLY]: { value: "monthly", label: "Mensuel" },
};

export const getRecurrenceFrequencyConfig = (freq) =>
  RECURRENCE_FREQUENCY_CONFIG[freq] || null;
export const getRecurrenceFrequencyLabel = (freq) =>
  getRecurrenceFrequencyConfig(freq)?.label ?? freq;

// ─── Week days ──────────────────────────────────────────────────

export const WEEK_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
export const WEEK_DAYS_EN = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const weekDayCodeToFr = (code) => {
  if (!code) return null;
  const idx = WEEK_DAYS_EN.indexOf(String(code).toLowerCase());
  return idx === -1 ? null : WEEK_DAYS[idx];
};

// ─── Event types ────────────────────────────────────────────────

export const EVENT_TYPES = {
  PRIVATE_LESSON: "private_lesson",
  SPECIAL: "special",
  BLOCKED: "blocked",
  GROUPED_LESSON: "grouped_lesson",
  COMPETITION: "competition",
};

export const EVENT_TYPE_CONFIG = {
  [EVENT_TYPES.PRIVATE_LESSON]: {
    value: EVENT_TYPES.PRIVATE_LESSON,
    label: "Cours particulier",
    description: "Cours individuel",
    icon: Icons.PrivateLesson,
    slotClass: "slot-private",
  },
  [EVENT_TYPES.SPECIAL]: {
    value: EVENT_TYPES.SPECIAL,
    label: "Spécial",
    description: "Événement spécial",
    icon: Icons.Lesson,
    slotClass: "slot-special",
  },
  [EVENT_TYPES.BLOCKED]: {
    value: EVENT_TYPES.BLOCKED,
    label: "Bloqué",
    description: "Créneau bloqué",
    icon: Icons.Blocked,
    slotClass: "slot-blocked",
  },
  [EVENT_TYPES.GROUPED_LESSON]: {
    value: EVENT_TYPES.GROUPED_LESSON,
    label: "Cours collectif",
    description: "Cours en groupe",
    icon: Icons.GroupLesson,
    slotClass: "slot-grouped",
  },
  [EVENT_TYPES.COMPETITION]: {
    value: EVENT_TYPES.COMPETITION,
    label: "Compétition",
    description: "Compétition",
    icon: Icons.Competition,
    slotClass: "slot-competition",
  },
};

export const getEventTypeConfig = (type) => EVENT_TYPE_CONFIG[type] || null;
export const getEventTypeLabel = (type) =>
  getEventTypeConfig(type)?.label ?? type;
export const getEventTypeIcon = (type) => getEventTypeConfig(type)?.icon;
export const getEventSlotClass = (type) =>
  getEventTypeConfig(type)?.slotClass ?? "slot-default";

export function getEventTypeOptions() {
  return Object.values(EVENT_TYPE_CONFIG).map(({ value, label }) => ({
    value,
    label,
  }));
}

export const isBlockedEvent = (event) =>
  event?.event_type === EVENT_TYPES.BLOCKED;
export const isPrivateLesson = (event) =>
  event?.event_type === EVENT_TYPES.PRIVATE_LESSON;
export const isGroupedLesson = (event) =>
  event?.event_type === EVENT_TYPES.GROUPED_LESSON;

// ─── Slot statuses ──────────────────────────────────────────────

export const SLOT_STATUSES = {
  SCHEDULED: "scheduled",
  CONFIRMED: "confirmed",
  CANCELLED: "cancelled",
};

const SLOT_STATUS_CONFIG = {
  [SLOT_STATUSES.SCHEDULED]: {
    value: "scheduled",
    label: "Planifié",
    icon: Icons.Clock,
    description: "Planifié mais non confirmé",
  },
  [SLOT_STATUSES.CONFIRMED]: {
    value: "confirmed",
    label: "Confirmé",
    icon: Icons.Check,
    description: "Confirmé",
  },
  [SLOT_STATUSES.CANCELLED]: {
    value: "cancelled",
    label: "Annulé",
    icon: Icons.Close,
    description: "Annulé",
  },
};

export const getStatusConfig = (status) =>
  SLOT_STATUS_CONFIG[status] || SLOT_STATUS_CONFIG[SLOT_STATUSES.SCHEDULED];
export const getStatusLabel = (status) =>
  getStatusConfig(status)?.label ?? status;
export const getStatusIcon = (status) => getStatusConfig(status)?.icon;

export function getStatusOptions() {
  return Object.values(SLOT_STATUS_CONFIG).map(({ value, label }) => ({
    value,
    label,
  }));
}

export const PARTICIPANT_STATUSES = {
  PENDING: "pending",
  PRESENT: "present",
  CANCELLED: "cancelled",
  ABSENT_UNJUSTIFIED: "absent_unjustified",
  EXCLUDED: "excluded",
  OBSERVER: "observer",
};

export const PARTICIPANT_STATUS_CONFIG = {
  pending: { label: "En attente", icon: Icons.Pending },
  present: { label: "Présent", icon: Icons.Present },
  cancelled: { label: "Annulé", icon: Icons.Cancelled },
  absent_unjustified: { label: "Absent", icon: Icons.Unjustified },
  excluded: { label: "Exclu", icon: Icons.Excluded },
  observer: { label: "Observateur", icon: Icons.Obverver },
};

export function getParticipantStatusLabel(status) {
  return PARTICIPANT_STATUS_CONFIG[status]?.label || status;
}

export function getParticipantCount(participants) {
  return participants.length;
}
